import React from 'react';
import {connect} from 'react-redux';
import _ from 'lodash';
import {Segment, Grid, Header, Divider} from 'semantic-ui-react';

import {connectDynamic} from 'go/components/dynamic/dynamicComponent';
import {
  safeAdd,
  safeMult,
  convertValueToDollar,
  safeAddReturnNonNegative,
} from 'common/utils';
import {FemaGoTable} from 'app-ui-componentlibrary';
import {BudgetUtils} from 'app-ui-componentlibrary/dist/go/components/common/BudgetUtils';
import {Alert} from 'componentLibrary';
import {BOCS} from 'common/requestDetails/constants.js';
import {safeSubstract} from 'app-ui-componentlibrary/dist/common/utils';
import {calculateManagementCostThreshold} from 'common/requestDetails/utils';
import {produceAppliedDiff} from 'framework/collaboration/diff.utils';

const DIRECT_CHARGES_KEY = 'totalDirectCharges';
const INDIRECT_CHARGES_KEY = 'totalIndirectCharges';
const TOTAL_CHARGES_KEY = 'totalCharges';
const APPSHARE_BREAKDOWN_PATH = 'share.appShare';
const REMARKS_PATH = 'nonFederal.remarks';
const PROGRAM_INCOME_PATH = 'nonFederal.programIncome';
const INDIRECT_CHARGES_BOCS = ['indirectCharges'];
const NON_FEDERAL_PATH = 'context.awardRecommendation.payload.budget.nonFederal';
const ENTITY_TYPE = 'awardRecommendation';
const REDUCTIONS_PATH = 'reductions';

const TOTAL_MANAGEMENT_COST = 'totalManagementCosts';

const BOC_CONFIGS = {
  personnel: {label: 'Personnel', order: 1},
  fringeBenefits: {label: 'Fringe benefits', order: 2},
  travel: {label: 'Travel', order: 3},
  equipment: {label: 'Equipment', order: 4},
  supplies: {label: 'Supplies', order: 5},
  contractual: {label: 'Contractual', order: 6},
  construction: {label: 'Construction', order: 7},
  other: {label: 'Other', order: 8},
};

// Program Income methods
const PI_DEDUCTION = 'DeductionFromTotalCosts';
const PI_APPLICATION_TO_NON_FEDSHARE = 'ApplicationToNonFedShare';
const PI_ADDITION = 'AdditionToAward';
const PI_NOT_ALLOWED = 'ProgramIncomeNotAllowed';

export class BudgetSummaryTable extends React.Component {
  constructor(props) {
    super(props);

    const modelTemplate = {
      applicant: 0,
      state: 0,
      other: 0,
      remarks: '',
      programIncome: 0,
      constructionProgramIncome: 0,
    };

    const appShareUpdated = _.get(props, 'budget.share.appShareUpdated');
    const nonFederalSaved = _.get(props, 'budget.nonFederal');
    const mergedModel = _.merge(modelTemplate, nonFederalSaved);
    this.state = {
      model: mergedModel,
      appShareUpdated: appShareUpdated,
    };
    if (!_.isEqual(mergedModel, nonFederalSaved) && !_.get(props, 'config.readOnly')) {
      this.saveNonFederal(mergedModel);
    }
  }

  componentDidUpdate = (prevProps) => {
    let appShareUpdated = _.get(this.props, 'budget.share.appShareUpdated');
    if (this.state.appShareUpdated !== appShareUpdated) {
      this.setState({
        appShareUpdated: appShareUpdated,
      });
    }
    // peer could have updated the field
    if (this.props.currentlyCollaborating) {
      const nonFederalProps = _.get(this.props, 'budget.nonFederal');
      const prevNonFederalProps = _.get(prevProps, 'budget.nonFederal');
      if (!_.isEqual(nonFederalProps, prevNonFederalProps)) {
        this.setState((prevState) => ({
          model: produceAppliedDiff(
            prevNonFederalProps,
            nonFederalProps,
            prevState?.model
          ),
        }));
      }
    }
  };

  renderDirectBoc = (aggregatedTotals, boc, numPeriods, hidePeriodBreakdown) => (
    <FemaGoTable.Row>
      <FemaGoTable.TextCell width={6}>
        {_.get(BOC_CONFIGS, boc, {}).label}
      </FemaGoTable.TextCell>
      {hidePeriodBreakdown ? (
        <FemaGoTable.NumberCell />
      ) : (
        _.times(numPeriods, (period) => (
          <FemaGoTable.NumberCell>
            {convertValueToDollar(_.get(aggregatedTotals, `${boc}.${period}`))}
          </FemaGoTable.NumberCell>
        ))
      )}
      <FemaGoTable.NumberCell>
        {convertValueToDollar(
          _.reduce(_.get(aggregatedTotals, boc), (overall, total) =>
            safeAdd(overall, total)
          )
        )}
      </FemaGoTable.NumberCell>
    </FemaGoTable.Row>
  );

  renderTotalCharges = (
    bocTotals,
    numPeriods,
    hidePeriodBreakdown,
    withReductions,
    reductions,
    programIncome
  ) => (
    <FemaGoTable.Row>
      <FemaGoTable.TextCell>
        <b>TOTAL</b>
      </FemaGoTable.TextCell>
      {hidePeriodBreakdown ? (
        <FemaGoTable.NumberCell />
      ) : (
        _.times(numPeriods, (period) => (
          <FemaGoTable.NumberCell>
            {convertValueToDollar(_.get(bocTotals, `${TOTAL_CHARGES_KEY}.${period}`))}
          </FemaGoTable.NumberCell>
        ))
      )}
      {withReductions ? (
        <FemaGoTable.NumberCell>
          {convertValueToDollar(
            safeSubstract(
              _.reduce(_.get(bocTotals, TOTAL_CHARGES_KEY), (acc, periodCharges) =>
                safeAddReturnNonNegative(acc, periodCharges)
              ),
              _.reduce(
                {...reductions, programIncome},
                (total, reduction) => safeAdd(total, reduction),
                0
              )
            )
          )}
        </FemaGoTable.NumberCell>
      ) : (
        <FemaGoTable.NumberCell>
          {convertValueToDollar(
            _.reduce(_.get(bocTotals, TOTAL_CHARGES_KEY), (acc, periodCharges) =>
              safeAddReturnNonNegative(acc, periodCharges)
            )
          )}
        </FemaGoTable.NumberCell>
      )}
    </FemaGoTable.Row>
  );

  renderIndirectCharges = (bocTotals, numPeriods, hidePeriodBreakdown) => (
    <FemaGoTable.Row background="tableDividerBorder-top">
      <FemaGoTable.TextCell>Indirect charges</FemaGoTable.TextCell>
      {hidePeriodBreakdown ? (
        <FemaGoTable.NumberCell />
      ) : (
        _.times(numPeriods, (period) => (
          <FemaGoTable.NumberCell>
            {convertValueToDollar(_.get(bocTotals, `${INDIRECT_CHARGES_KEY}.${period}`))}
          </FemaGoTable.NumberCell>
        ))
      )}
      <FemaGoTable.NumberCell>
        {convertValueToDollar(
          _.reduce(_.get(bocTotals, INDIRECT_CHARGES_KEY), (acc, periodCharges) =>
            safeAdd(acc, periodCharges)
          )
        )}
      </FemaGoTable.NumberCell>
    </FemaGoTable.Row>
  );

  renderDirectCharges = (
    directChargeBocs,
    bocTotals,
    numPeriods,
    hidePeriodBreakdown
  ) => (
    <React.Fragment>
      {_.map(directChargeBocs, (boc) =>
        this.renderDirectBoc(bocTotals, boc, numPeriods, hidePeriodBreakdown)
      )}
      <FemaGoTable.Row>
        <FemaGoTable.TextCell>
          <b>Total direct charges</b>
        </FemaGoTable.TextCell>
        {hidePeriodBreakdown ? (
          <FemaGoTable.NumberCell />
        ) : (
          _.times(numPeriods, (period) => (
            <FemaGoTable.NumberCell>
              {convertValueToDollar(_.get(bocTotals, `${DIRECT_CHARGES_KEY}.${period}`))}
            </FemaGoTable.NumberCell>
          ))
        )}
        <FemaGoTable.NumberCell>
          {convertValueToDollar(
            _.reduce(_.get(bocTotals, DIRECT_CHARGES_KEY), (acc, periodCharges) =>
              safeAdd(acc, periodCharges)
            )
          )}
        </FemaGoTable.NumberCell>
      </FemaGoTable.Row>
    </React.Fragment>
  );

  renderProgramIncome = (
    income = 0,
    numPeriods,
    readOnly = true,
    hidePeriodBreakdown,
    hideProgramIncomeWarning,
    budgetSummary
  ) => {
    const {foHandleProgIncome, budget, context} = this.props;
    const yearOverYear =
      foHandleProgIncome != undefined
        ? _.get(
            context,
            'fundingOpportunityData.configData.budget.effectiveTimePeriodType'
          ) == 'Year'
          ? true
          : false
        : false;

    let lastPeriodAppShare;
    let previousTotalCharges;
    if (yearOverYear) {
      lastPeriodAppShare = [];
      previousTotalCharges = [];
      for (let i = 0; i < numPeriods; i++) {
        lastPeriodAppShare.push(_.get(budget, `${APPSHARE_BREAKDOWN_PATH}.${i}`));

        previousTotalCharges.push(
          safeAdd(
            _.get(budgetSummary, `${DIRECT_CHARGES_KEY}.${i}`),
            _.get(budgetSummary, `${INDIRECT_CHARGES_KEY}.${i}`)
          )
        );
      }
    } else {
      lastPeriodAppShare = _.get(budget, `${APPSHARE_BREAKDOWN_PATH}.${numPeriods - 1}`);
      previousTotalCharges = safeAdd(
        _.get(budgetSummary, `${DIRECT_CHARGES_KEY}.${numPeriods - 1}`),
        _.get(budgetSummary, `${INDIRECT_CHARGES_KEY}.${numPeriods - 1}`)
      );
    }

    let warnProgramIncomeExceeds;
    if (yearOverYear) {
      for (let i = 0; i < numPeriods; i++) {
        if (!warnProgramIncomeExceeds) {
          warnProgramIncomeExceeds =
            (_.toNumber(lastPeriodAppShare[i]) < 0 ||
              _.toNumber(this.state.model.programIncome[i]) > previousTotalCharges[i]) &&
            !(
              _.isEqual(foHandleProgIncome, PI_ADDITION) ||
              _.isEqual(foHandleProgIncome, PI_NOT_ALLOWED)
            );
        }
      }
    } else {
      warnProgramIncomeExceeds =
        (_.toNumber(lastPeriodAppShare) < 0 ||
          _.toNumber(this.state.model.programIncome) > previousTotalCharges) &&
        !(
          _.isEqual(foHandleProgIncome, PI_ADDITION) ||
          _.isEqual(foHandleProgIncome, PI_NOT_ALLOWED)
        );
    }
    // IF hidePeriodBreakdown THEN Row -> 3 cells: Header, Empty cell, Cell with total value
    // IF NOT hidePeriodBreakdown THEN Row -> Header, numPeriod number of cells, Cell with total

    const calculateIncomeTotal = (programIncome, numPeriods) => {
      let incomeTotal = 0;
      for (let i = 0; i < numPeriods; i++) {
        if (programIncome[i] != null && programIncome[i] != undefined) {
          incomeTotal = incomeTotal + _.toNumber(programIncome[i]);
        }
      }
      return incomeTotal;
    };

    const wholeIncome = (
      <FemaGoTable.DollarCell
        width={4}
        value={_.get(this.state.model, 'programIncome', 0)}
        title="Required field: number"
        id={'budgetProgramIncome'}
        name={'programIncome'}
        displayError={this.getSpecificError('programIncome').displayError}
        handleBlur={() => {
          this.saveNonFederal(this.state.model);
        }}
        handleChange={(name, value) => {
          let updatedModel = _.cloneDeep(this.state.model);
          _.set(updatedModel, name, value);
          this.setState({model: updatedModel});
        }}
      />
    );

    let periodArray = [];
    for (let i = 0; i < numPeriods; i++) {
      let obj = {
        label: `program_income_year-${i + 1}`,
        value: 0,
      };
      periodArray.push(obj);
    }
    return (
      <React.Fragment>
        <FemaGoTable.Row background="tableDividerBorder-top">
          <FemaGoTable.TextCell>Program income</FemaGoTable.TextCell>
          {!yearOverYear && (
            <FemaGoTable.TextCell
              colSpan={hidePeriodBreakdown ? 1 : numPeriods}
            ></FemaGoTable.TextCell>
          )}

          {readOnly ? (
            <FemaGoTable.NumberCell>
              {convertValueToDollar(income)}
            </FemaGoTable.NumberCell>
          ) : yearOverYear ? (
            <React.Fragment>
              {_.map(periodArray, (period, index) => (
                <FemaGoTable.DollarCell
                  width={2}
                  value={_.get(this.state.model.programIncome, `${index}`, 0)}
                  title="Required field: number"
                  id={`budgetProgramIncome_${index + 1}`}
                  name={`incomePeriod_${index + 1}`}
                  displayError={
                    this.getSpecificError(`incomePeriod_${index + 1}`).displayError
                  }
                  handleBlur={() => {
                    this.saveNonFederal(this.state.model);
                  }}
                  handleChange={(name, value) => {
                    let updatedModel = _.cloneDeep(this.state.model);
                    _.set(updatedModel, `programIncome[${index}]`, value);
                    this.setState({model: updatedModel});
                  }}
                >
                  ;
                </FemaGoTable.DollarCell>
              ))}
              <FemaGoTable.NumberCell>
                {convertValueToDollar(
                  calculateIncomeTotal(this.state.model.programIncome, numPeriods)
                )}
              </FemaGoTable.NumberCell>
            </React.Fragment>
          ) : (
            wholeIncome
          )}
        </FemaGoTable.Row>
        {(_.toNumber(this.state.model.programIncome) > 0 ||
          calculateIncomeTotal(this.state.model.programIncome, numPeriods) > 0) &&
          !hideProgramIncomeWarning && (
            <FemaGoTable.Row>
              <FemaGoTable.TextCell colSpan={16}>
                <Alert
                  header="Program income reported"
                  content="Although not common, recipients may generate income in the course of carrying out grant-supported activities during the period of performance under the FEMA grant award. This is referred to as program income. This income can be used to defray program costs, where appropriate, consistent with 2 CFR § 200.307. This response should be $0 unless the recipient anticipates generating program income during the period of performance."
                  type="warning"
                  dismissable={false}
                />
                {warnProgramIncomeExceeds && (
                  <Alert
                    header="Program income exceeds shares"
                    content="Federal and/or Non-federal shares are displayed as negative because of reported program income."
                    type="warning"
                    dismissable={false}
                  />
                )}
              </FemaGoTable.TextCell>
            </FemaGoTable.Row>
          )}
      </React.Fragment>
    );
  };
  renderManagementCost = (
    budgetSummary,
    maxPercentageOfManagementCost,
    numPeriods,
    hidePeriodBreakdown,
    disableManagementCostAlert
  ) => {
    let percentBasedOnManagementCost = _.get(this.props, 'percentBasedOnManagementCost');
    let totalCharges = _.reduce(
      _.get(budgetSummary, TOTAL_CHARGES_KEY),
      (acc, periodCharges) => safeAdd(acc, periodCharges)
    );
    let managementTotalCost =
      _.reduce(_.get(budgetSummary, TOTAL_MANAGEMENT_COST), (acc, periodCharges) =>
        safeAdd(acc, periodCharges)
      ) || 0;
    // if total is a negative value, force it to zero
    let nonManagementTotalCost = totalCharges - managementTotalCost;
    nonManagementTotalCost = nonManagementTotalCost < 0 ? 0 : nonManagementTotalCost;
    let managementCostThreshold =
      nonManagementTotalCost * 0.01 * maxPercentageOfManagementCost;
    let data = this.props.budget;
    let fundingStreams = this.props.fundingStreams;
    let costshareFS = this.props.costshareFS;
    if (percentBasedOnManagementCost) {
      const resultObj = calculateManagementCostThreshold({
        data,
        fundingStreams,
        costshareFS,
      });
      managementCostThreshold = resultObj.managementCostThreshold;
    }
    return (
      <React.Fragment>
        <FemaGoTable.Row background="tableDividerBorder-top">
          <FemaGoTable.TextCell>
            Total applicant management costs (optional)
            <i>
              {' '}
              (Maximum allowable management cost:{' '}
              {convertValueToDollar(managementCostThreshold)})
            </i>
          </FemaGoTable.TextCell>

          <FemaGoTable.TextCell colSpan={hidePeriodBreakdown ? 1 : numPeriods}>
            {!disableManagementCostAlert && (
              <Alert
                size="mini"
                content="Applicant management cost is optional. If you want to add a management cost to your application, then add a management cost subapplication to this application."
                type="info"
                dismissable={false}
              />
            )}
          </FemaGoTable.TextCell>

          <FemaGoTable.NumberCell>
            {convertValueToDollar(managementTotalCost ? managementTotalCost : 0)}
          </FemaGoTable.NumberCell>
        </FemaGoTable.Row>
      </React.Fragment>
    );
  };
  renderCostShareSection = (
    budget,
    numPeriods,
    hidePeriodBreakdown,
    fedSharePath,
    appSharePath
  ) => {
    const fedShare = _.get(budget, fedSharePath ? fedSharePath : 'fedShare');
    const appShare = _.get(budget, appSharePath ? appSharePath : 'appShare');

    const sharesUnknown = _.size(fedShare) === 0 || _.size(appShare) === 0;
    return (
      <React.Fragment>
        <FemaGoTable.Row background="tableDividerBorder-top">
          <FemaGoTable.TextCell colSpan={16}>
            <b>Total Federal and Non-federal resources</b>
            {this.state.appShareUpdated && (
              <Alert
                size="mini"
                header="Approved exception to cost share rules"
                type="warning"
                dismissable={false}
              >
                The cost share rules for this grant have been adjusted per the approved
                waiver attachment.
              </Alert>
            )}
          </FemaGoTable.TextCell>
        </FemaGoTable.Row>

        {[
          {label: 'Federal resources', key: fedSharePath ? fedSharePath : 'fedShare'},
          {label: 'Non-federal resources', key: appSharePath ? appSharePath : 'appShare'},
        ].map((source) => (
          <FemaGoTable.Row>
            <FemaGoTable.TextCell>{source.label}</FemaGoTable.TextCell>
            {_.map(_.get(budget, source.key), (periodTotal, i) => (
              <React.Fragment>
                <FemaGoTable.NumberCell>
                  {hidePeriodBreakdown ? '' : convertValueToDollar(periodTotal)}
                </FemaGoTable.NumberCell>
              </React.Fragment>
            ))}
            {sharesUnknown ? (
              <React.Fragment>
                <FemaGoTable.TextCell />
                <FemaGoTable.TextCell textAlign={'right'}>{'-'}</FemaGoTable.TextCell>
              </React.Fragment>
            ) : (
              <FemaGoTable.NumberCell>
                {convertValueToDollar(
                  _.reduce(
                    _.get(budget, source.key),
                    (acc, total) => safeAddReturnNonNegative(acc, total),
                    0
                  )
                )}
              </FemaGoTable.NumberCell>
            )}
          </FemaGoTable.Row>
        ))}
      </React.Fragment>
    );
  };

  renderNonFederalSection = (readOnly, budget, numPeriods, hidePeriodBreakdown) => (
    <React.Fragment>
      <FemaGoTable.Row background="tableDividerBorder-top">
        <FemaGoTable.TextCell colSpan={16}>
          <b>Non-federal resources</b>
        </FemaGoTable.TextCell>
      </FemaGoTable.Row>
      {[
        {label: 'Applicant', key: 'applicant'},
        {label: 'State', key: 'state'},
        {label: 'Local', key: 'local'},
        {label: 'Other sources', key: 'other'},
      ].map((source) => {
        const {displayError, errorMessages} = this.getSpecificError(source.key);
        return (
          <FemaGoTable.Row>
            <FemaGoTable.TextCell>{source.label}</FemaGoTable.TextCell>
            {hidePeriodBreakdown ? (
              <FemaGoTable.TextCell />
            ) : (
              _.times(numPeriods, () => <FemaGoTable.TextCell />)
            )}
            {readOnly ? (
              <FemaGoTable.NumberCell>
                {convertValueToDollar(_.get(budget, ['nonFederal', source.key]))}
              </FemaGoTable.NumberCell>
            ) : (
              <FemaGoTable.DollarCellEnhanced
                value={_.get(this.state.model, source.key, 0)}
                title={`${source.label}: Required field`}
                id={source.key}
                name={source.key}
                displayError={displayError}
                errorMessages={errorMessages}
                handleBlur={() => {
                  this.saveNonFederal(this.state.model);
                }}
                handleChange={(name, value) => {
                  let updatedModel = _.cloneDeep(this.state.model);
                  _.set(updatedModel, name, value);
                  this.setState({model: updatedModel});
                }}
                width={2}
              />
            )}
          </FemaGoTable.Row>
        );
      })}
    </React.Fragment>
  );

  renderRemarks = (remarks, readOnly, hidePeriodBreakdown, numPeriods) => {
    const {displayError, errorMessages} = this.getSpecificError('remarks');
    return (
      <FemaGoTable.Row>
        <FemaGoTable.TextCell>Remarks</FemaGoTable.TextCell>
        {hidePeriodBreakdown ? (
          <FemaGoTable.TextCell />
        ) : (
          _.times(numPeriods, () => <FemaGoTable.TextCell />)
        )}
        {/* this field is optional, but the textareafield table cell doesn't support optional prop yet TODO */}
        {readOnly ? (
          <FemaGoTable.TextCell textAlign={'right'}>{remarks}</FemaGoTable.TextCell>
        ) : (
          <FemaGoTable.TextAreaCellEnhanced
            width={4}
            rows={2}
            value={_.get(this.state.model, 'remarks')}
            id={'budgetNonFedRemarks'}
            name={'Remarks'}
            displayError={displayError}
            errorMessages={errorMessages}
            handleBlur={() => {
              this.saveNonFederal(this.state.model);
            }}
            handleChange={(e) => {
              let updatedModel = _.cloneDeep(this.state.model);
              _.set(updatedModel, 'remarks', e.target.value);
              this.setState({model: updatedModel});
            }}
          />
        )}
      </FemaGoTable.Row>
    );
  };

  renderHeaders = (numPeriods, hidePeriodBreakdown) => (
    <FemaGoTable.Row>
      <FemaGoTable.HeaderCell>Object class categories</FemaGoTable.HeaderCell>
      {hidePeriodBreakdown ? (
        <FemaGoTable.HeaderCell />
      ) : (
        _.times(numPeriods, (period) => (
          <FemaGoTable.HeaderCell type="number">Year {period + 1}</FemaGoTable.HeaderCell>
        ))
      )}
      <FemaGoTable.HeaderCell type="number">Total</FemaGoTable.HeaderCell>
    </FemaGoTable.Row>
  );

  getSpecificError = (field) => {
    const errParent = _.find(this.props.validationErrors, (errorObj) => {
      return _.has(errorObj, field);
    });
    const errorsForField = _.get(errParent, field);
    const firstError = _.find(errorsForField, (err) => !err.isValid) || {
      isValid: true,
    };
    return {
      displayError: !firstError.isValid,
      errorMessages: firstError.message,
    };
  };

  saveNonFederal = (value) => {
    const {config} = this.props;
    const {nonFederalPath, entityType} = config;
    const constructionProgramIncome = _.get(
      this.props,
      'budget.nonFederal.constructionProgramIncome'
    );
    if (constructionProgramIncome) {
      value = {
        ...value,
        constructionProgramIncome,
      };
    }

    if (nonFederalPath !== undefined) {
      this.props.saveEntityData(
        nonFederalPath || NON_FEDERAL_PATH,
        entityType || ENTITY_TYPE,
        value,
        'budget'
      );
    }
  };

  render() {
    let {
      budget,
      config,
      fundingStreams,
      foConfig,
      fedSharePath,
      appSharePath,
    } = this.props;
    if (budget && foConfig) {
      const {extras} = budget;
      const bocs = BOCS;
      const {budgetSummary, share} = budget;
      const {
        hidePeriodBreakdown,
        isProgramIncomeLast = false,
        hideProgramIncomeWarning = false,
        showManagementCost = false,
        maxPercentageOfManagementCost = 10,
        readOnlyNonFed,
        readOnlyRemarks,
        readOnlyProgramIncome,
        header,
        hideHeader,
        disableManagementCostAlert,
        withReductions,
      } = config;
      const readOnly = !!config.readOnly;
      const isNonFedReadOnly = _.isUndefined(readOnlyNonFed) ? readOnly : readOnlyNonFed;
      const isRemarksReadOnly = _.isUndefined(readOnlyRemarks)
        ? readOnly
        : readOnlyRemarks;
      const isProgramIncomeReadOnly = _.isUndefined(readOnlyProgramIncome)
        ? readOnly
        : readOnlyProgramIncome;
      const directChargeBocs = _.sortBy(
        _.difference(bocs, INDIRECT_CHARGES_BOCS),
        (a) => _.get(BOC_CONFIGS, a, {}).order
      );

      let numPeriods = _.get(budget, APPSHARE_BREAKDOWN_PATH, []).length;
      let colSpan = hidePeriodBreakdown ? 3 : numPeriods + 2;
      let reductions = _.get(budget, REDUCTIONS_PATH, {});
      return (
        <React.Fragment>
          {!hideHeader && (
            <Header as="h4" id={this.props.id}>
              {header ? header : 'Budget summary'}
            </Header>
          )}
          <FemaGoTable summary="Budget summary table">
            <FemaGoTable.Header>
              {this.renderHeaders(numPeriods, hidePeriodBreakdown)}
            </FemaGoTable.Header>
            <FemaGoTable.Body>
              {this.renderDirectCharges(
                directChargeBocs,
                budgetSummary,
                numPeriods,
                hidePeriodBreakdown
              )}
              {this.renderIndirectCharges(budgetSummary, numPeriods, hidePeriodBreakdown)}
              {this.renderTotalCharges(budgetSummary, numPeriods, hidePeriodBreakdown)}
              {!isProgramIncomeLast &&
                this.renderProgramIncome(
                  _.get(budget, PROGRAM_INCOME_PATH),
                  numPeriods,
                  isProgramIncomeReadOnly,
                  hidePeriodBreakdown,
                  hideProgramIncomeWarning,
                  budgetSummary
                )}
              {!isProgramIncomeLast && withReductions && (
                <React.Fragment>
                  <FemaGoTable.Row>
                    <FemaGoTable.TextCell>Insurance reduction</FemaGoTable.TextCell>
                    <FemaGoTable.TextCell colSpan={1}></FemaGoTable.TextCell>
                    <FemaGoTable.NumberCell>
                      {convertValueToDollar(_.get(reductions, 'receivedInsurance', 0))}
                    </FemaGoTable.NumberCell>
                  </FemaGoTable.Row>
                  <FemaGoTable.Row>
                    <FemaGoTable.TextCell>Other sources</FemaGoTable.TextCell>
                    <FemaGoTable.TextCell colSpan={1}></FemaGoTable.TextCell>
                    <FemaGoTable.NumberCell>
                      {convertValueToDollar(_.get(reductions, 'otherFundingSources', 0))}
                    </FemaGoTable.NumberCell>
                  </FemaGoTable.Row>
                  {this.renderTotalCharges(
                    budgetSummary,
                    numPeriods,
                    hidePeriodBreakdown,
                    withReductions,
                    reductions,
                    _.get(budget, PROGRAM_INCOME_PATH)
                  )}
                </React.Fragment>
              )}
              {this.renderNonFederalSection(
                isNonFedReadOnly,
                budget,
                numPeriods,
                hidePeriodBreakdown
              )}
              {this.renderRemarks(
                _.get(budget, REMARKS_PATH),
                isRemarksReadOnly,
                hidePeriodBreakdown,
                numPeriods
              )}
              {this.renderCostShareSection(
                share,
                numPeriods,
                hidePeriodBreakdown,
                fedSharePath,
                appSharePath
              )}
              {this.renderTotalCharges(
                budgetSummary,
                numPeriods,
                hidePeriodBreakdown,
                withReductions,
                reductions,
                _.get(budget, PROGRAM_INCOME_PATH)
              )}
              {isProgramIncomeLast &&
                this.renderProgramIncome(
                  _.get(budget, PROGRAM_INCOME_PATH),
                  numPeriods,
                  isProgramIncomeReadOnly,
                  hidePeriodBreakdown,
                  hideProgramIncomeWarning,
                  budgetSummary
                )}
              {isProgramIncomeLast && withReductions && (
                <React.Fragment>
                  <FemaGoTable.Row>
                    <FemaGoTable.TextCell>Insurance reduction</FemaGoTable.TextCell>
                    <FemaGoTable.TextCell colSpan={1}></FemaGoTable.TextCell>
                    <FemaGoTable.NumberCell>
                      {convertValueToDollar(_.get(reductions, 'receivedInsurance', 0))}
                    </FemaGoTable.NumberCell>
                  </FemaGoTable.Row>
                  <FemaGoTable.Row>
                    <FemaGoTable.TextCell>Other sources</FemaGoTable.TextCell>
                    <FemaGoTable.TextCell colSpan={1}></FemaGoTable.TextCell>
                    <FemaGoTable.NumberCell>
                      {convertValueToDollar(_.get(reductions, 'otherFundingSources', 0))}
                    </FemaGoTable.NumberCell>
                  </FemaGoTable.Row>
                  {this.renderTotalCharges(
                    budgetSummary,
                    numPeriods,
                    hidePeriodBreakdown,
                    withReductions,
                    reductions,
                    _.get(budget, PROGRAM_INCOME_PATH)
                  )}
                </React.Fragment>
              )}
              {showManagementCost &&
                this.renderManagementCost(
                  budgetSummary,
                  maxPercentageOfManagementCost,
                  numPeriods,
                  hidePeriodBreakdown,
                  disableManagementCostAlert
                )}
            </FemaGoTable.Body>
          </FemaGoTable>
          {!_.isEmpty(_.get(extras, 'message')) && (
            <Alert
              size="small"
              header=""
              content={_.get(extras, 'message.text')}
              type={_.get(extras, 'message.type', 'info')}
              dismissable={false}
            />
          )}
        </React.Fragment>
      );
    } else {
      return null;
    }
  }
}

export const mapStateToProps = (state) => ({
  currentlyCollaborating: !!_.get(state, 'dynamicComponent.collaboration.roomList', ''),
});

export default connect(mapStateToProps)(connectDynamic(BudgetSummaryTable));
